import React, { useState, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from '../components/ui/separator';
import { useToast } from '../hooks/use-toast';
import { apiRequest, queryClient } from '../lib/queryClient';
import { useAuth } from '../hooks/use-auth';
import { 
  CreditCard, 
  Crown, 
  Users, 
  Zap, 
  Calendar, 
  CheckCircle,
  XCircle,
  AlertTriangle,
  AlertCircle,
  ArrowUpCircle,
  ArrowDownCircle,
  Home,
  RefreshCw
} from "lucide-react";
import { Redirect } from "wouter";

interface SubscriptionInfo {
  contractor: {
    subscriptionPlan: string;
    subscriptionStatus: string;
  };
  plan: {
    planName: string;
    displayName: string;
    priceMonthly: number;
    clientLimit: number | null;
    aiUsageLimit: number | null;
    hasTimeClockAccess: boolean;
    hasStripeIntegration: boolean;
    hasCustomPortal: boolean;
    hasBrandedPortal: boolean;
    features: string[];
  };
  usage: {
    currentClientCount: number;
    maxClients: number | null;
    clientsRemaining: number | string;
    currentAiUsage: number;
    maxAiUsage: number | null;
    aiUsageRemaining: number | string;
  };
  billing: {
    planStartDate: string | null;
    planEndDate: string | null;
    stripeCustomerId: string | null;
    stripeSubscriptionId: string | null;
  };
  availablePlans: Array<{
    name: string;
    displayName: string;
    price: string;
    clientLimit: number | null;
    aiUsageLimit: number | null;
    hasTimeClockAccess: boolean;
    hasStripeIntegration: boolean;
    hasCustomPortal: boolean;
    hasBrandedPortal: boolean;
  }>;
}

const BillingPage = () => {
  console.log('🚀 BILLING PAGE COMPONENT LOADING');
  const { user } = useAuth();
  const { toast } = useToast();
  const [isUpgrading, setIsUpgrading] = useState(false);
  
  console.log('🔍 Billing page - user state:', user);
  
  // Check for trial_expired parameter
  const urlParams = new URLSearchParams(window.location.search);
  const trialExpired = urlParams.get('trial_expired') === 'true';
  
  // Check for success/cancel/newUser query parameters from Stripe redirect
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const canceled = urlParams.get('canceled');
    const newUser = urlParams.get('newUser');
    
    if (success) {
      toast({
        title: "Subscription Successful!",
        description: "Your subscription has been activated. Welcome to Remodra!",
      });
      // Clean up URL
      window.history.replaceState({}, '', '/billing');
    }
    
    if (canceled) {
      toast({
        title: "Checkout Canceled",
        description: "Your subscription was not processed. You can try again anytime.",
        variant: "destructive"
      });
      // Clean up URL
      window.history.replaceState({}, '', '/billing');
    }
    
    if (newUser) {
      toast({
        title: "🎉 Welcome to Remodra!",
        description: "Please select a plan to get started. You'll get a 7-day free trial when you subscribe.",
        duration: 7000,
      });
      // Clean up URL
      window.history.replaceState({}, '', '/billing');
    }
  }, [toast]);

  // Only make API calls if user is authenticated
  const { data: subscriptionInfo, isLoading, error } = useQuery<SubscriptionInfo>({
    queryKey: ['/api/protected/subscription/status'],
    enabled: !!user, // Only run query if user exists
  });

  // Auto-sync from Stripe when Billing page loads (keeps DB in sync; no manual "Refresh" needed)
  const hasAutoSynced = useRef(false);
  useEffect(() => {
    if (!user || hasAutoSynced.current) return;
    hasAutoSynced.current = true;
    (async () => {
      try {
        const res = await apiRequest('POST', '/api/protected/admin/sync-stripe', {});
        const data = await res.json();
        if (data.synced || data.success) {
          queryClient.invalidateQueries({ queryKey: ['/api/protected/subscription/status'] });
        }
      } catch {
        hasAutoSynced.current = false; // allow retry on next visit if failed
      }
    })();
  }, [user]);

  console.log('🔍 Billing page - subscription API state:', { 
    isLoading, 
    hasData: !!subscriptionInfo, 
    error: error?.message 
  });

  if (!user) {
    console.log('🔍 Billing page - No user, showing auth required');
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Authentication Required
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Please log in to access your billing information.
          </p>
          <Button onClick={() => window.location.href = '/simple-login'}>
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  console.log('🔍 Billing page - User authenticated, rendering billing page');

  const upgradeMutation = useMutation({
    mutationFn: async (planName: string) => {
      const response = await apiRequest('POST', '/api/protected/subscription/change-plan', { plan: planName });
      return response.json();
    },
    onSuccess: (data) => {
      if (data.success && data.user) {
        // Update localStorage with new user data
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        const updatedUser = { ...currentUser, plan: data.user.plan };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        
        toast({
          title: "Plan Updated! 🎉",
          description: `Your plan has been changed to ${data.user.plan}. The page will reload to update your features.`,
        });
        
        // Reload page to update sidebar/features
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        toast({
          title: "Plan Updated",
          description: "Your subscription plan has been updated successfully.",
        });
        queryClient.invalidateQueries({ queryKey: ['/api/protected/subscription/status'] });
      }
    },
    onError: (error: any) => {
      toast({
        title: "Plan Change Failed",
        description: error.message || "Failed to change plan. Please try again.",
        variant: "destructive",
      });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest('POST', '/api/stripe/cancel-subscription', {
        contractorId: user?.id
      });
      return await response.json();
    },
    onSuccess: () => {
      toast({
        title: "Subscription Cancelled",
        description: "Your subscription will remain active until the end of your billing period.",
      });
      queryClient.invalidateQueries({ queryKey: ['/api/protected/subscription/status'] });
    },
    onError: (error: any) => {
      toast({
        title: "Cancellation Failed",
        description: error.message || "Failed to cancel subscription. Please contact support.",
        variant: "destructive",
      });
    },
  });

  const syncFromStripeMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest('POST', '/api/protected/admin/sync-stripe', {});
      return await response.json();
    },
    onSuccess: (data: { synced?: boolean; success?: boolean; message?: string; error?: string }) => {
      if (data.synced) {
        toast({
          title: "Subscription updated",
          description: "Your plan and trial end date have been refreshed from Stripe.",
        });
        queryClient.invalidateQueries({ queryKey: ['/api/protected/subscription/status'] });
        window.history.replaceState({}, '', '/billing');
        window.location.reload();
      } else if (data.success === false) {
        toast({
          title: "No change",
          description: data.message || data.error || "No active or trialing subscription found in Stripe for this account.",
          variant: "destructive",
        });
        queryClient.invalidateQueries({ queryKey: ['/api/protected/subscription/status'] });
      }
      // When success: true but synced: false (e.g. no Stripe customer yet), no toast; UI already shows plan
    },
    onError: (error: any) => {
      const msg = error?.message || String(error);
      toast({
        title: "Sync failed",
        description: msg.includes('No Stripe customer') ? "This account isn't linked to a Stripe customer yet. Subscribe to a plan below to link it." : msg,
        variant: "destructive",
      });
    },
  });

  // Stripe Price IDs for each plan (from Stripe Dashboard / env)
  // Optional: VITE_STRIPE_PRICE_BUSINESS_TRIAL = $0/full-discount Business price for testing
  const STRIPE_PRICE_IDS = {
    basic: import.meta.env.VITE_STRIPE_PRICE_BASIC || 'price_basic_placeholder',
    pro: import.meta.env.VITE_STRIPE_PRICE_PRO || 'price_pro_placeholder',
    business: import.meta.env.VITE_STRIPE_PRICE_BUSINESS || 'price_business_placeholder',
    businessTrial: import.meta.env.VITE_STRIPE_PRICE_BUSINESS_TRIAL || '',
  };

  // Check if Stripe price IDs are configured (only the 3 required plans; businessTrial is optional)
  const requiredPriceIds = [STRIPE_PRICE_IDS.basic, STRIPE_PRICE_IDS.pro, STRIPE_PRICE_IDS.business];
  const isStripeConfigured = requiredPriceIds.every(priceId => 
    priceId && !priceId.includes('placeholder')
  );

  // Debug: price IDs (VITE_* are set at build time in Vercel)
  console.log('🔍 Stripe Price IDs:', STRIPE_PRICE_IDS);
  console.log('🔍 Is Stripe Configured:', isStripeConfigured);

  const handleUpgrade = async (planName: string, priceIdOverride?: string) => {
    try {
      setIsUpgrading(true);
      
      // Use override when provided (e.g. Business free-trial price); otherwise use plan's price
      const priceId = priceIdOverride ?? STRIPE_PRICE_IDS[planName as keyof typeof STRIPE_PRICE_IDS];
      
      if (!priceId || priceId.includes('placeholder')) {
        toast({
          title: "Configuration Required",
          description: "Stripe price IDs need to be configured. Please contact support.",
          variant: "destructive"
        });
        setIsUpgrading(false);
        return;
      }
      
      console.log('🔍 Creating checkout session for plan:', planName, 'priceId:', priceId);
      
      // Call our API to create a Stripe checkout session
      const response = await apiRequest('POST', '/api/stripe/create-checkout-session', {
        priceId,
        planName,
        email: user?.email
      });
      
      const data = await response.json();
      
      if (data.success && data.url) {
        console.log('✅ Redirecting to Stripe checkout:', data.url);
        // Redirect to Stripe checkout
        window.location.href = data.url;
      } else {
        throw new Error(data.error || 'Failed to create checkout session');
      }
      
    } catch (error: unknown) {
      console.error('❌ Upgrade error:', error);
      const message = error instanceof Error ? error.message : 'Failed to start checkout.';
      const isNoSuchPrice = typeof message === 'string' && message.includes('No such price');
      const isTestKeyWithLivePrice = typeof message === 'string' && message.includes('test mode key was used');
      const isNoSuchCustomer = typeof message === 'string' && message.includes('No such customer');
      toast({
        title: "Upgrade Failed",
        description: isNoSuchPrice || isTestKeyWithLivePrice
          ? "Stripe is in test mode but your prices are live. In Vercel → Settings → Environment Variables, set STRIPE_SECRET_KEY to your live key (sk_live_...) for Production, then redeploy so Change Plan redirects to Stripe's payment page."
          : isNoSuchCustomer
            ? "Your account had an old payment profile. Please try upgrading again—it should work now."
            : message,
        variant: "destructive"
      });
      setIsUpgrading(false);
    }
  };


  const handleCancel = () => {
    if (window.confirm("Are you sure you want to cancel your subscription? You'll lose access to premium features at the end of your billing period.")) {
      cancelMutation.mutate();
    }
  };

  const handleManagePaymentMethods = async () => {
    try {
      if (!subscriptionInfo.stripeCustomerId) {
        toast({
          title: "No Customer ID",
          description: "Customer ID not found. Please contact support.",
          variant: "destructive"
        });
        return;
      }

      const response = await apiRequest('POST', '/api/stripe/create-customer-portal', {
        customerId: subscriptionInfo.stripeCustomerId,
        returnUrl: `${window.location.origin}/billing`
      });

      const data = await response.json();
      
      if (data.success && data.url) {
        window.open(data.url, '_blank');
      } else {
        throw new Error(data.message || 'Failed to create customer portal session');
      }
    } catch (error) {
      console.error('Error opening customer portal:', error);
      toast({
        title: "Error",
        description: "Failed to open payment methods. Please try again.",
        variant: "destructive"
      });
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  // Show configuration warning if Stripe isn't set up
  if (!isStripeConfigured) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-900">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
                <h2 className="text-xl font-semibold text-red-800 dark:text-red-200">Configuration Required</h2>
              </div>
              <p className="text-red-700 dark:text-red-300 mb-4">
                Stripe price IDs need to be configured in Vercel environment variables to enable subscription payments.
              </p>
              <div className="bg-white dark:bg-slate-800 rounded-lg p-4 mb-4">
                <h3 className="font-semibold text-slate-800 dark:text-slate-200 mb-2">Required Environment Variables:</h3>
                <ul className="text-sm text-slate-600 dark:text-slate-400 space-y-1">
                  <li><code className="bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded">VITE_STRIPE_PRICE_BASIC</code></li>
                  <li><code className="bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded">VITE_STRIPE_PRICE_PRO</code></li>
                  <li><code className="bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded">VITE_STRIPE_PRICE_BUSINESS</code></li>
                </ul>
                <p className="text-xs text-slate-500 dark:text-slate-500 mt-2">Optional: <code className="bg-slate-100 dark:bg-slate-700 px-1 rounded">VITE_STRIPE_PRICE_BUSINESS_TRIAL</code> for a $0/full-discount Business price.</p>
              </div>
              <p className="text-sm text-red-600 dark:text-red-400">
                Please contact your administrator to set up Stripe integration.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!subscriptionInfo) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600">
            <CardContent className="pt-6">
              <div className="text-center">
                <AlertTriangle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                <h2 className="text-xl font-semibold mb-2 text-slate-800 dark:text-slate-200">Unable to Load Billing Information</h2>
                <p className="text-slate-600 dark:text-slate-400">
                  Please try refreshing the page or contact support if the problem persists.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const getStatusIcon = (status: string | undefined) => {
    if (!status) return <AlertCircle className="h-5 w-5 text-amber-500" />;
    switch (status) {
      case 'active':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'cancelled':
      case 'canceled':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'trialing':
        return <Calendar className="h-5 w-5 text-blue-500" />;
      case 'trial':
        return <Calendar className="h-5 w-5 text-blue-500" />;
      case 'no_subscription':
        return <AlertCircle className="h-5 w-5 text-amber-500" />;
      default:
        return <AlertTriangle className="h-5 w-5 text-green-500" />;
    }
  };

  const getStatusColor = (status: string | undefined) => {
    if (!status) return 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-200';
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'cancelled':
      case 'canceled':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'trialing':
      case 'trial':
        return 'bg-blue-100 text-blue-800 dark:bg-slate-800 dark:text-slate-200';
      case 'no_subscription':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200';
      default:
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    }
  };

  const getStatusLabel = (status: string | undefined) => {
    if (status === 'no_subscription') return 'Choose a plan';
    if (!status) return '—';
    return status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ');
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Header */}
          <div className="remodra-card p-6 mb-6">
            <div className="flex flex-col lg:flex-row justify-between items-center gap-4">
              <div className="flex items-center gap-4">
                <img 
                  src="/remodra-logo.png" 
                  alt="Remodra Logo" 
                  className="h-12 w-12 rounded-full object-cover"
                />
                <div>
                  <h1 className="text-2xl font-bold text-green-500">
                    Billing & Subscription
                  </h1>
                  <p className="text-slate-300 text-sm">
                    Manage your subscription and billing information
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => window.location.href = '/dashboard'}
                  className="remodra-button-outline"
                >
                  <Home className="h-4 w-4 mr-2" />
                  Dashboard
                </Button>
              </div>
            </div>
          </div>

          {/* Trial Expired Banner */}
          {trialExpired && (
            <Card className="bg-red-50 dark:bg-red-900/20 border-4 border-red-500">
              <CardContent className="pt-6">
                <div className="text-center py-6">
                  <div className="flex justify-center mb-4">
                    <div className="p-4 rounded-full bg-red-100 dark:bg-red-900/30">
                      <AlertCircle className="h-12 w-12 text-red-600 dark:text-red-400" />
                    </div>
                  </div>
                  <h2 className="text-3xl font-bold text-red-800 dark:text-red-200 mb-3">
                    🔒 Trial Expired - Payment Required
                  </h2>
                  <p className="text-lg text-red-700 dark:text-red-300 mb-2">
                    Your free trial has ended.
                  </p>
                  <p className="text-red-600 dark:text-red-400 font-semibold mb-6">
                    Add payment below to continue using Remodra
                  </p>
                  <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border-2 border-red-300 dark:border-red-700 max-w-md mx-auto">
                    <p className="text-slate-800 dark:text-slate-200 text-sm font-bold mb-2">
                      ❌ All features are currently locked
                    </p>
                    <p className="text-slate-600 dark:text-slate-400 text-xs">
                      You will not be able to access clients, projects, estimates, or any other features until you add a payment method and activate your subscription.
                    </p>
                    <p className="text-slate-600 dark:text-slate-400 text-xs mt-3">
                      If you have an active subscription or trial in Stripe (e.g. with a coupon), click below to refresh your status.
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      className="mt-3 border-amber-500 text-amber-700 hover:bg-amber-50 dark:text-amber-300 dark:hover:bg-amber-900/30"
                      onClick={() => syncFromStripeMutation.mutate()}
                      disabled={syncFromStripeMutation.isPending}
                    >
                      <RefreshCw className={`h-4 w-4 mr-2 ${syncFromStripeMutation.isPending ? 'animate-spin' : ''}`} />
                      {syncFromStripeMutation.isPending ? 'Refreshing…' : 'Refresh from Stripe'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* New User Welcome Message */}
          {window.location.search.includes('newUser=true') && (
            <Card className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-full bg-green-100 dark:bg-green-900/30">
                    <Crown className="h-6 w-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-green-800 dark:text-green-200">
                      Welcome to Remodra! 🎉
                    </h3>
                    <p className="text-green-700 dark:text-green-300 text-sm">
                      Choose a plan below to start your 7-day free trial (no charge until trial ends)
                    </p>
                  </div>
                </div>
                <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-green-200 dark:border-green-700">
                  <h4 className="font-semibold text-slate-800 dark:text-slate-200 mb-2">What happens next?</h4>
                  <ul className="text-sm text-slate-600 dark:text-slate-400 space-y-1">
                    <li>• Select your preferred plan (Basic, Pro, or Business)</li>
                    <li>• Enter your payment information — you won't be charged until after your 7-day trial</li>
                    <li>• Enjoy full access to all features for 7 days</li>
                    <li>• After the trial, your subscription continues automatically (cancel anytime)</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Current Plan - guard contractor so we don't crash on 403/malformed response */}
          {subscriptionInfo.contractor == null ? (
            <Card className="bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600">
              <CardContent className="pt-6">
                <p className="text-slate-600 dark:text-slate-400">Loading subscription details…</p>
              </CardContent>
            </Card>
          ) : (
          <Card className="bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-slate-800 dark:text-slate-200">
                <Crown className="h-5 w-5 text-green-500" />
                Current Plan
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-bold text-slate-800 dark:text-slate-200">{subscriptionInfo.plan?.displayName ?? '—'}</h3>
                <p className="text-slate-600 dark:text-slate-400">
                  ${subscriptionInfo.plan?.priceMonthly ?? 0}/month
                </p>
              </div>
              <div className="flex items-center gap-2">
                {getStatusIcon(subscriptionInfo.contractor?.subscriptionStatus)}
                <Badge className={getStatusColor(subscriptionInfo.contractor?.subscriptionStatus)}>
                  {getStatusLabel(subscriptionInfo.contractor?.subscriptionStatus)}
                </Badge>
              </div>
            </div>

            {/* Trial Information - ONLY show if trial is active AND not expired */}
            {subscriptionInfo.contractor?.subscriptionStatus === 'trialing' && !trialExpired && (
              <div className="bg-blue-50 dark:bg-slate-800/50 border border-blue-200 dark:border-slate-700 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  <h4 className="font-semibold text-blue-800 dark:text-blue-200">Free Trial Active</h4>
                </div>
                <p className="text-blue-700 dark:text-blue-300 text-sm">
                  You're on a free trial of the {subscriptionInfo.plan.displayName}. Your trial end date is shown below. You'll be charged when the trial ends unless you cancel.
                </p>
                {subscriptionInfo.billing.planEndDate && (
                  <p className="text-blue-600 dark:text-blue-400 text-xs mt-2">
                    Trial ends: {new Date(subscriptionInfo.billing.planEndDate).toLocaleDateString()}
                  </p>
                )}
              </div>
            )}

            <Separator />

            {/* Plan Details */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold mb-3 text-slate-800 dark:text-slate-200">Plan Features</h4>
                <div className="space-y-2 text-slate-700 dark:text-slate-300">
                  <div className="flex items-center justify-between">
                    <span>Client Limit</span>
                    <span className="font-medium text-slate-800 dark:text-slate-200">
                      {subscriptionInfo.plan.clientLimit || 'Unlimited'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>AI Analysis</span>
                    <span className="font-medium text-slate-800 dark:text-slate-200">
                      {subscriptionInfo.plan.aiUsageLimit ? 
                        `${subscriptionInfo.plan.aiUsageLimit}/month` : 
                        subscriptionInfo.plan.aiUsageLimit === null ? 'Unlimited' : 'Not included'
                      }
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Time Clock</span>
                    <span className="font-medium">
                      {subscriptionInfo.plan.hasTimeClockAccess ? 'Included' : 'Not included'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Stripe Integration</span>
                    <span className="font-medium">
                      {subscriptionInfo.plan.hasStripeIntegration ? 'Included' : 'Not included'}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-3">Current Usage</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span>Active Clients</span>
                    <span className="font-medium">
                      {subscriptionInfo.usage.currentClientCount}
                      {subscriptionInfo.plan.clientLimit && 
                        ` / ${subscriptionInfo.plan.clientLimit}`
                      }
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>AI Usage This Month</span>
                    <span className="font-medium">
                      {subscriptionInfo.usage.currentAiUsage}
                      {subscriptionInfo.plan.aiUsageLimit && 
                        ` / ${subscriptionInfo.plan.aiUsageLimit}`
                      }
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Billing Dates */}
            {subscriptionInfo.billing.planStartDate && (
              <div>
                <h4 className="font-semibold mb-3">Billing Information</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">Current Period Started</span>
                    <p className="font-medium">
                      {new Date(subscriptionInfo.billing.planStartDate).toLocaleDateString()}
                    </p>
                  </div>
                  {subscriptionInfo.billing.planEndDate && (
                    <div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {subscriptionInfo.contractor?.subscriptionStatus === 'cancelled' ? 'Access Ends' : 'Next Billing Date'}
                      </span>
                      <p className="font-medium">
                        {new Date(subscriptionInfo.billing.planEndDate).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
          )}

        {/* Available Plans */}
        <Card className="bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-slate-800 dark:text-slate-200">
              <ArrowUpCircle className="h-5 w-5 text-blue-500" />
              Available Plans
            </CardTitle>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
              Upgrade to unlock more: AI that wins bids, roof reports, and time tracking for your team.
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-6">
              {subscriptionInfo.availablePlans.map((plan) => {
                const isCurrent = plan.name === subscriptionInfo.plan.planName;
                const isUpgrade = parseInt(plan.price) > subscriptionInfo.plan.priceMonthly;
                const isTrialing = subscriptionInfo.contractor?.subscriptionStatus === 'trialing';
                // Only show payment button if: current plan + trial mode + trial expired flag in URL
                const needsPayment = isCurrent && isTrialing && trialExpired;
                
                return (
                  <Card key={plan.name} className={`relative bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 ${isCurrent ? 'ring-2 ring-green-500' : ''}`}>
                    {isCurrent && !needsPayment && (
                      <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                        <Badge className="bg-green-500 text-white">Current Plan</Badge>
                      </div>
                    )}
                    
                    <CardHeader className="text-center">
                      <CardTitle className="text-slate-800 dark:text-slate-200">{plan.displayName}</CardTitle>
                      <div className="text-2xl font-semibold text-green-500">${plan.price}<span className="text-sm font-normal text-slate-600 dark:text-slate-400">/month</span></div>
                    </CardHeader>
                    
                    <CardContent>
                      <div className="space-y-2 mb-4 text-slate-700 dark:text-slate-300">
                        <div className="flex items-center justify-between text-sm">
                          <span>Clients</span>
                          <span className="font-medium text-slate-800 dark:text-slate-200">{plan.clientLimit || 'Unlimited'}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span>AI Analysis</span>
                          <span className="font-medium text-slate-800 dark:text-slate-200">
                            {plan.aiUsageLimit ? `${plan.aiUsageLimit}/month` : 
                             plan.aiUsageLimit === null ? 'Unlimited' : 'None'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span>Time Clock</span>
                          <span className="font-medium text-slate-800 dark:text-slate-200">{plan.hasTimeClockAccess ? 'Yes' : 'No'}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span>Stripe</span>
                          <span className="font-medium text-slate-800 dark:text-slate-200">{plan.hasStripeIntegration ? 'Yes' : 'No'}</span>
                        </div>
                      </div>
                      
                      {/* Show button for: non-current plans OR current plan in trial mode */}
                      {(!isCurrent || needsPayment) && (
                        <div className="space-y-2">
                          <Button 
                            onClick={() => handleUpgrade(plan.name)}
                            disabled={isUpgrading}
                            className={needsPayment ? "remodra-button w-full bg-red-600 hover:bg-red-700" : (isUpgrade ? "remodra-button w-full" : "remodra-button-outline w-full")}
                          >
                            {needsPayment ? (
                              <>
                                <CreditCard className="h-4 w-4 mr-2" />
                                Add Payment to {plan.displayName}
                              </>
                            ) : isUpgrade ? (
                              <>
                                <ArrowUpCircle className="h-4 w-4 mr-2" />
                                Subscribe to {plan.displayName}
                              </>
                            ) : (
                              <>
                                <ArrowDownCircle className="h-4 w-4 mr-2" />
                                Change to {plan.displayName}
                              </>
                            )}
                          </Button>
                          {/* Optional: full-discount Business price (e.g. $0 trial) - use when env is set */}
                          {plan.name === 'business' && STRIPE_PRICE_IDS.businessTrial && (
                            <Button
                              variant="outline"
                              onClick={() => handleUpgrade('business', STRIPE_PRICE_IDS.businessTrial)}
                              disabled={isUpgrading}
                              className="remodra-button-outline w-full border-green-500 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20"
                            >
                              Start free trial (full access)
                            </Button>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Billing Actions */}
        <Card className="bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-slate-800 dark:text-slate-200">
              <CreditCard className="h-5 w-5 text-green-500" />
              Billing Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Button variant="outline" className="w-full">
                Download Invoice History
              </Button>
              
              {subscriptionInfo.stripeCustomerId && (
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={handleManagePaymentMethods}
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  Manage Payment Methods
                </Button>
              )}
              
              {subscriptionInfo.contractor?.subscriptionStatus === 'active' && ( 
                <Button 
                  variant="destructive" 
                  onClick={handleCancel}
                  disabled={cancelMutation.isPending}
                  className="w-full"
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Cancel Subscription
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
        </div>
      </div>
    </div>
  );
};

export default BillingPage;